"use client";

import { useEffect, useState } from "react";

// action types
type Action =
  | {
      type: "ADD_DIALOG";
      dialog: DialogItem;
    }
  | {
      type: "UPDATE_DIALOG";
      dialog: Partial<DialogItem> & { id: string };
    }
  | {
      type: "DISMISS_DIALOG";
      dialogId?: string;
    }
  | {
      type: "REMOVE_DIALOG";
      dialogId?: string;
    };

interface DialogItem {
  id: string;
  title?: React.ReactNode;
  message?: React.ReactNode;
  onAccept?: () => void;
  onReject?: () => void;
  open: boolean;
}

interface State {
  dialogs: DialogItem[];
}

let dialogCount = 0;

function genId() {
  dialogCount = (dialogCount + 1) % Number.MAX_SAFE_INTEGER;
  return dialogCount.toString();
}

const dialogTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const DIALOG_REMOVE_DELAY = 100; // Adjust as needed

const addToRemoveQueue = (dialogId: string) => {
  if (dialogTimeouts.has(dialogId)) {
    return;
  }

  const timeout = setTimeout(() => {
    dialogTimeouts.delete(dialogId);
    dispatch({
      type: "REMOVE_DIALOG",
      dialogId: dialogId,
    });
  }, DIALOG_REMOVE_DELAY);

  dialogTimeouts.set(dialogId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_DIALOG":
      return {
        ...state,
        dialogs: [action.dialog, ...state.dialogs],
      };
    case "UPDATE_DIALOG":
      return {
        ...state,
        dialogs: state.dialogs.map((d) =>
          d.id === action.dialog.id ? { ...d, ...action.dialog } : d,
        ),
      };
    case "DISMISS_DIALOG": {
      const { dialogId } = action;

      // Side effects
      if (dialogId) {
        addToRemoveQueue(dialogId);
      } else {
        state.dialogs.forEach((dialog) => {
          addToRemoveQueue(dialog.id);
        });
      }

      return {
        ...state,
        dialogs: state.dialogs.map((d) =>
          d.id === dialogId || dialogId === undefined
            ? {
                ...d,
                open: false,
              }
            : d,
        ),
      };
    }
    case "REMOVE_DIALOG":
      if (action.dialogId === undefined) {
        return {
          ...state,
          dialogs: [],
        };
      }
      return {
        ...state,
        dialogs: state.dialogs.filter((d) => d.id !== action.dialogId),
      };
    default:
      return state;
  }
};

const listeners: ((state: State) => void)[] = [];

let memoryState: State = { dialogs: [] };

export function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

interface DialogProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  onAccept?: () => void;
  onReject?: () => void;
}

function dialog({ title, message, onAccept, onReject }: DialogProps) {
  const id = genId();

  const update = (props: Partial<DialogItem>) =>
    dispatch({
      type: "UPDATE_DIALOG",
      dialog: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_DIALOG", dialogId: id });

  dispatch({
    type: "ADD_DIALOG",
    dialog: {
      id,
      title,
      message,
      onAccept,
      onReject,
      open: true,
    },
  });

  return {
    id,
    dismiss,
    update,
  };
}

function useDialog() {
  const [state, setState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return {
    ...state,
    dialog,
    dismiss: (dialogId?: string) =>
      dispatch({ type: "DISMISS_DIALOG", dialogId }),
  };
}

export { dialog, useDialog };
